import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, RefreshCw, Save, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, extractApiError } from "../lib/api";
import PageHeader from "../components/layout/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import StatusPill from "../components/status/StatusPill";
import { useAuth } from "../lib/auth-context";
import { applyBrandColor } from "../lib/brand-context";
import { buildEmailTestPayload } from "../lib/emailTestPayload";
import { ROLE_LABELS } from "../lib/labels";
import { mailSendToastMessage } from "../lib/mailLabels";
import { getDefaultSettingsTab, getVisibleSettingsTabs } from "../lib/settings";
import { getProviderInfo } from "../lib/settingsInfo";

export default function SettingsPage() {
  const { tenant, role, user, isSuperAdmin, capabilities } = useAuth();
  const [settings, setSettings] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailTestTo, setEmailTestTo] = useState("");
  const [emailTestFrom, setEmailTestFrom] = useState("");
  const [emailTestFromName, setEmailTestFromName] = useState("");
  const [emailTestSubject, setEmailTestSubject] = useState("FyreCRM Emailit Test");
  const [emailTestMessage, setEmailTestMessage] = useState(
    "Dies ist eine Testmail aus FyreCRM.\n\nTest-Link: https://example.com/"
  );
  const [emailTestHtmlLinkUrl, setEmailTestHtmlLinkUrl] = useState("");
  const [emailTestHtmlLinkLabel, setEmailTestHtmlLinkLabel] = useState("");
  const [emailTestTrackingLoads, setEmailTestTrackingLoads] = useState(false);
  const [emailTestTrackingClicks, setEmailTestTrackingClicks] = useState(false);
  const [emailTestSending, setEmailTestSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNext, setPwNext] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const visibleTabs = useMemo(
    () => getVisibleSettingsTabs({ role, isSuperAdmin, capabilities }) || [],
    [role, isSuperAdmin, capabilities],
  );
  const defaultTab = useMemo(() => getDefaultSettingsTab(visibleTabs), [visibleTabs]);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const canSeeTenantSettings = visibleTabs.includes("tenant");
  const canSeeEmailStatus = visibleTabs.includes("email");

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!canSeeTenantSettings) {
        setSettings(null);
        setEmailStatus(null);
        return;
      }
      const requests = [api.get("/api/settings/tenant")];
      if (canSeeEmailStatus) {
        requests.push(api.get("/api/settings/email-status"));
      }
      const responses = await Promise.all(requests);
      let responseIndex = 0;
      const tenantResponse = responses[responseIndex++];
      const emailStatusResponse = canSeeEmailStatus ? responses[responseIndex++] : null;
      setSettings(tenantResponse.data);
      setEmailStatus(emailStatusResponse?.data || null);
      const activeProviderFrom =
        emailStatusResponse?.data?.providers?.find(
          (p) => p.provider === emailStatusResponse?.data?.active_provider
        )?.from_email || "";
      if (activeProviderFrom) {
        setEmailTestFrom((prev) => prev || activeProviderFrom);
      }
      applyBrandColor(tenantResponse.data.branding_color);
    } catch (e) {
      toast.error(extractApiError(e, "Einstellungen konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, [canSeeEmailStatus, canSeeTenantSettings]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { branding_color, sender_name, reply_to_email, default_project } = settings;
      const r = await api.patch("/api/settings/tenant", { branding_color, sender_name, reply_to_email, default_project });
      setSettings(r.data);
      applyBrandColor(r.data.branding_color);
      toast.success("Einstellungen gespeichert.");
    } catch (e) {
      toast.error(extractApiError(e, "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmailTest = async () => {
    setEmailTestSending(true);
    try {
      const r = await api.post(
        "/api/settings/email-test",
        buildEmailTestPayload({
          toEmail: emailTestTo,
          fromEmail: emailTestFrom,
          fromName: emailTestFromName,
          subject: emailTestSubject,
          message: emailTestMessage,
          htmlLinkUrl: emailTestHtmlLinkUrl,
          htmlLinkLabel: emailTestHtmlLinkLabel,
          trackingLoads: emailTestTrackingLoads,
          trackingClicks: emailTestTrackingClicks,
        })
      );
      const message = mailSendToastMessage({ ok: Boolean(r.data?.ok), isTest: true });
      if (r.data?.ok) {
        toast.success(message);
      } else {
        toast.error(message);
      }
    } catch (e) {
      toast.error(extractApiError(e, "E-Mail konnte nicht gesendet werden."));
    } finally {
      setEmailTestSending(false);
    }
  };

  const handleRegenerate = async () => {
    if (!settings) return;
    if (!window.confirm("Wirklich einen neuen Public-Form-Schlüssel generieren? Bestehende öffentliche Formularlinks werden ungültig.")) return;
    try {
      const r = await api.post("/api/settings/tenant/regenerate-form-key");
      setSettings(r.data);
      toast.success("Neuer Schlüssel generiert.");
    } catch (e) {
      toast.error(extractApiError(e, "Aktion fehlgeschlagen."));
    }
  };

  const handleCopy = (text, label = "Wert") => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${label} kopiert.`))
      .catch(() => toast.error("Kopieren fehlgeschlagen."));
  };

  const handlePasswordChange = async (event) => {
    event?.preventDefault();
    if (!pwCurrent || !pwNext) {
      toast.error("Bitte aktuelles und neues Passwort eingeben.");
      return;
    }
    if (pwNext.length < 8) {
      toast.error("Neues Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (pwNext !== pwConfirm) {
      toast.error("Passwort-Bestätigung stimmt nicht überein.");
      return;
    }
    setPwSaving(true);
    try {
      await api.patch("/api/auth/password", {
        current_password: pwCurrent,
        new_password: pwNext,
        new_password_confirm: pwConfirm,
      });
      setPwCurrent("");
      setPwNext("");
      setPwConfirm("");
      toast.success("Passwort geändert.");
    } catch (e) {
      toast.error(extractApiError(e, "Passwort konnte nicht geändert werden."));
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground py-12 text-center">Wird geladen …</div>;

  const publicFormUrl = settings ? `${window.location.origin}/demo/${settings.public_form_key}` : "";

  return (
    <div>
      <PageHeader title="Einstellungen" description={`Mandant: ${tenant?.name || "—"}`} />

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="settings-tabs">
        <TabsList className="justify-start">
          {visibleTabs.includes("tenant") && (
            <TabsTrigger value="tenant" data-testid="settings-tab-tenant">Mandant</TabsTrigger>
          )}
          {visibleTabs.includes("email") && (
            <TabsTrigger value="email" data-testid="settings-tab-email">E-Mail</TabsTrigger>
          )}
          <TabsTrigger value="account" data-testid="settings-tab-account">Konto</TabsTrigger>
          <TabsTrigger value="about" data-testid="settings-tab-about">Info</TabsTrigger>
        </TabsList>

        {visibleTabs.includes("tenant") && settings && (
          <TabsContent value="tenant" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Mandanten-Grunddaten</CardTitle>
                <CardDescription>Diese Daten werden für Branding und E-Mail-Header verwendet.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Branding-Farbe">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.branding_color || "#EF694C"}
                      onChange={(e) => setSettings({ ...settings, branding_color: e.target.value })}
                      className="w-10 h-10 rounded-lg border border-border cursor-pointer bg-card"
                      data-testid="tenant-branding-color-input"
                    />
                    <Input value={settings.branding_color || ""} onChange={(e) => setSettings({ ...settings, branding_color: e.target.value })} className="font-mono" />
                  </div>
                </F>
                <F label="Standard-Projekt / Produkt">
                  <Input data-testid="tenant-default-project-input" value={settings.default_project || ""} onChange={(e) => setSettings({ ...settings, default_project: e.target.value })} placeholder="z.B. OptiMES Prime" />
                </F>
                <F label="Absendername (E-Mail)">
                  <Input value={settings.sender_name || ""} onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })} />
                </F>
                <F label="Reply-To E-Mail">
                  <Input value={settings.reply_to_email || ""} onChange={(e) => setSettings({ ...settings, reply_to_email: e.target.value })} placeholder="z.B. vertrieb@firma.de" />
                </F>
              </CardContent>
              <div className="flex justify-end px-6 pb-5">
                <Button onClick={handleSave} disabled={saving} data-testid="settings-save-button">
                  <Save className="w-4 h-4 mr-1.5" /> {saving ? "Speichern …" : "Speichern"}
                </Button>
              </div>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Öffentlicher Formular-Schlüssel</CardTitle>
                <CardDescription>
                  Mit diesem Link können Interessenten öffentlich Leads für Ihren Mandanten anlegen.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input data-testid="public-form-key" readOnly value={settings.public_form_key} className="font-mono text-xs" />
                  <Button variant="outline" data-testid="public-form-key-copy-button" onClick={() => handleCopy(settings.public_form_key, "Schlüssel")}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid="public-form-url" readOnly value={publicFormUrl} className="text-xs" />
                  <Button variant="outline" onClick={() => handleCopy(publicFormUrl, "URL")}><Copy className="w-4 h-4" /></Button>
                  <Button variant="outline" onClick={() => window.open(publicFormUrl, "_blank")}><ExternalLink className="w-4 h-4" /></Button>
                </div>
                <Button variant="destructive" onClick={handleRegenerate} data-testid="public-form-key-regenerate-button">
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Neuen Schlüssel generieren
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {visibleTabs.includes("email") && (
          <TabsContent value="email" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>E-Mail-Service</CardTitle>
                <CardDescription>
                  Aktiver Provider: <strong>{emailStatus?.active_provider}</strong>. Konfiguration ausschließlich über Umgebungsvariablen.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {emailStatus?.providers?.map((p) => {
                  const isActive = p.provider === emailStatus.active_provider;
                  return (
                    <div key={p.provider} className="obsidian p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" style={{ color: "var(--brand)" }} />
                          <span className="text-sm font-display font-semibold uppercase tracking-[0.12em]">{p.provider}</span>
                        </div>
                        <div className="flex gap-1">
                          {isActive && <StatusPill tone="info">Aktiv</StatusPill>}
                          <StatusPill tone={p.configured ? "success" : "muted"}>
                            {p.configured ? "Konfiguriert" : "Nicht konfiguriert"}
                          </StatusPill>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-2.5 leading-relaxed">{p.reason || "—"}</div>
                      {p.from_email && (
                        <div className="text-xs mt-1.5">
                          Absender: <span className="font-mono">{p.from_name ? `${p.from_name} <${p.from_email}>` : p.from_email}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Testmail</CardTitle>
                <CardDescription>
                  Versand einer Testmail über den aktuell aktiven Provider. Diese Funktion ist nur für Super-Admins verfügbar.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label="Empfänger-E-Mail">
                    <Input
                      value={emailTestTo}
                      onChange={(e) => setEmailTestTo(e.target.value)}
                      placeholder="test@example.com"
                      data-testid="email-test-to"
                    />
                  </F>
                  <F label="Absender-E-Mail">
                    <Input
                      value={emailTestFrom}
                      onChange={(e) => setEmailTestFrom(e.target.value)}
                      placeholder="noreply@mail.fyreweb.de"
                      data-testid="email-test-from"
                    />
                  </F>
                  <F label="Absendername">
                    <Input
                      value={emailTestFromName}
                      onChange={(e) => setEmailTestFromName(e.target.value)}
                      placeholder="FyreCRM"
                      data-testid="email-test-from-name"
                    />
                  </F>
                  <F label="Betreff">
                    <Input
                      value={emailTestSubject}
                      onChange={(e) => setEmailTestSubject(e.target.value)}
                      placeholder="FyreCRM Emailit Test"
                      data-testid="email-test-subject"
                    />
                  </F>
                </div>

                <F label="Nachricht">
                  <Textarea
                    rows={5}
                    value={emailTestMessage}
                    onChange={(e) => setEmailTestMessage(e.target.value)}
                    data-testid="email-test-message"
                  />
                </F>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label="HTML-Testlink URL">
                    <Input
                      value={emailTestHtmlLinkUrl}
                      onChange={(e) => setEmailTestHtmlLinkUrl(e.target.value)}
                      placeholder="https://example.com/test"
                      data-testid="email-test-html-link-url"
                    />
                  </F>
                  <F label="HTML-Testlink Label">
                    <Input
                      value={emailTestHtmlLinkLabel}
                      onChange={(e) => setEmailTestHtmlLinkLabel(e.target.value)}
                      placeholder="Testlink öffnen"
                      data-testid="email-test-html-link-label"
                    />
                  </F>
                </div>

                <p className="text-xs text-muted-foreground">
                  Nur für Tracking-Tests. Der Link wird als sicherer HTML-Anker in die Testmail eingefügt.
                </p>

                <div className="flex flex-col gap-2">
                  <label className="flex items-start gap-3 text-sm">
                    <Checkbox
                      checked={emailTestTrackingLoads}
                      onCheckedChange={(checked) => setEmailTestTrackingLoads(Boolean(checked))}
                      data-testid="email-test-tracking-loads"
                      className="mt-0.5"
                    />
                    <span>Öffnungstracking testen</span>
                  </label>
                  <label className="flex items-start gap-3 text-sm">
                    <Checkbox
                      checked={emailTestTrackingClicks}
                      onCheckedChange={(checked) => setEmailTestTrackingClicks(Boolean(checked))}
                      data-testid="email-test-tracking-clicks"
                      className="mt-0.5"
                    />
                    <span>Klicktracking testen</span>
                  </label>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={handleSendEmailTest}
                    disabled={emailTestSending}
                    data-testid="email-test-send-button"
                  >
                    {emailTestSending ? "Senden …" : "Testmail senden"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="account" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Konto</CardTitle>
              <CardDescription>Verwalten Sie Ihre persönlichen Zugangsdaten.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4" data-testid="settings-change-password-form">
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={user?.email || ""}
                  readOnly
                  className="sr-only"
                  tabIndex={-1}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label="Aktuelles Passwort">
                    <Input
                      type="password"
                      name="current-password"
                      autoComplete="current-password"
                      value={pwCurrent}
                      onChange={(e) => setPwCurrent(e.target.value)}
                    />
                  </F>
                  <div />
                  <F label="Neues Passwort">
                    <Input
                      type="password"
                      name="new-password"
                      autoComplete="new-password"
                      value={pwNext}
                      onChange={(e) => setPwNext(e.target.value)}
                    />
                  </F>
                  <F label="Neues Passwort bestätigen">
                    <Input
                      type="password"
                      name="new-password-confirm"
                      autoComplete="new-password"
                      value={pwConfirm}
                      onChange={(e) => setPwConfirm(e.target.value)}
                    />
                  </F>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={pwSaving} data-testid="settings-change-password-button">
                    {pwSaving ? "Speichern …" : "Passwort ändern"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="about" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Informationen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 text-sm">
              <div className="space-y-2">
                <div>
                  <strong>Angemeldet als:</strong> {user?.full_name} ({user?.email})
                </div>
                <div>
                  <strong>Rolle:</strong> {ROLE_LABELS[role] || role}
                </div>
                <div>
                  <strong>Mandant:</strong> {tenant?.name} ({tenant?.slug})
                </div>
              </div>

              <div className="pt-4 border-t border-border/60 space-y-2">
                <div className="text-sm font-semibold">Über FyreCRM</div>
                <div className="text-muted-foreground">Command Center für den Vertrieb</div>
              </div>

              <div className="pt-4 border-t border-border/60 space-y-3">
                <div className="text-sm font-semibold">Anbieter</div>
                {(() => {
                  const provider = getProviderInfo();
                  return (
                    <div className="space-y-1">
                      <div className="font-medium">{provider.nameLine}</div>
                      {provider.addressLines.map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                      <div className="pt-2 space-y-1">
                        <div>
                          Telefon:{" "}
                          <a
                            className="underline underline-offset-2"
                            href={provider.phone.href}
                          >
                            {provider.phone.label}
                          </a>
                        </div>
                        <div>
                          E-Mail:{" "}
                          <a
                            className="underline underline-offset-2"
                            href={provider.email.href}
                          >
                            {provider.email.label}
                          </a>
                        </div>
                        <div className="pt-1 flex flex-wrap gap-x-3 gap-y-1">
                          {provider.websites.map((site) => (
                            <a
                              key={site.href}
                              className="underline underline-offset-2 text-muted-foreground"
                              href={site.href}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {site.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
